import { ErrorInfo } from 'react';
import { Outlet } from 'react-router';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClient } from '@tanstack/react-query';
import {
  DocumentScanner,
  Message,
  RouteOutlined,
  LocalGasStation,
  Logout as LogoutIcon,
  GpsFixed,
  Domain,
  AccountCircle,
  InfoOutlined,
  MoreHoriz,
  TrackChanges,
  DownloadForOffline,
  InsertChart,
  AlternateEmailOutlined,
  EditNote,
  SettingsOutlined,
} from '@mui/icons-material';
import { Badge } from '@mui/material';

import { PopupType } from '@/types';
import {
  ContactsPopup,
  MessagesPopup,
  More,
  TrackingPopup,
  RoutesPopup,
  RefuelsPopup,
  StreamChatPopup,
} from '@/components';
import Error from '@/components/common/Error';
import ReportPopup from '@/components/report/ReportPopup';
import StreamChatMenuIcon from '@/components/stream-chat/StreamChatMenuIcon';
import TachographPopup from '@/components/tachograph/fileDownload/TachographPopup';
import { NewFeatureBadge } from '@/components/common/NewFeatureBadge';
import FeedbackForm from '@/components/contacts/FeedbackForms';
import { TachographDiagramPopup } from '@/components/tachograph/diagram/TachographDiagramPopup';
import SettingsPopup from '@/components/settings/SettingsPopup';
import SysinfoPopup from '@/components/sysinfo/SysinfoPopup';
import EtransportIcon from '@/components/etransport/utils/EtransportSvgIcons';
import EtransportPopup from '@/components/etransport/Etransport';
import { Map } from '@/components';
import { Logout } from './Logout';
import { User } from '@/tmp';
import SharePopup from '@/components/tracking/SharePopup';

const newMessageCount = 0;

export function getAdditionalElements(
  closePopup: () => Promise<void>,
  isBottom: boolean,
  hasTachograph?: boolean
): PopupType[] {
  const additionalElements: PopupType[] = [
    {
      title: 'mainMenu.reports',
      icon: (
        <NewFeatureBadge releaseDate="2026-01-01">
          <DocumentScanner fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      element: <ReportPopup closePopup={closePopup} isBottom={isBottom} />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      requiredExtraFeature: 'extrafeature_pluto_report_tab',
      dataQaElementId: 'more-list-item-reports',
      level: 1,
      path: '/reports',
    },
    {
      title: 'mainMenu.etransport',
      icon: (
        <NewFeatureBadge releaseDate="2026-02-20">
          <EtransportIcon fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      element: <EtransportPopup close={close} data-qa-element-id="etransport-popup" />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: isBottom ? 1 : 2,
      path: isBottom ? '/etransport' : 'etransport',
      requiredExtraFeature: 'extrafeature_pluto_etransport',
      dataQaElementId: 'more-list-item-etransport',
    },
    {
      title: 'mainMenu.contacts',
      icon: (
        <NewFeatureBadge releaseDate="2026-01-20">
          <Domain fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      showToken: true,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-contacts',
      children: contactSubmenu(closePopup, isBottom),
      level: -1,
      path: '/contacts',
    },
    {
      title: 'mainMenu.profile',
      icon: (
        <NewFeatureBadge releaseDate="2026-01-25">
          <AccountCircle fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      showToken: false,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-profile',
      children: profileSubmenu(closePopup, isBottom),
      level: -1,
      path: '/profile',
    },
  ] satisfies PopupType[];

  if (hasTachograph) {
    additionalElements.unshift({
      title: 'mainMenu.tachograph',
      icon: (
        <NewFeatureBadge releaseDate="2026-01-10">
          <TrackChanges fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      showToken: false,
      mainFunction: true,
      showMobile: true,
      dataQaElementId: 'more-list-item-tachograph',
      children: tachographSubmenu(closePopup, isBottom),
      level: 1,
      path: '/tachograph',
    });
  }

  return additionalElements;
}

function tachographSubmenu(closePopup: () => Promise<void>, isBottom: boolean): PopupType[] {
  const subMenu = [
    {
      title: 'mainMenu.tachographSubmenu.fileDownload',
      icon: (
        <NewFeatureBadge releaseDate="2026-01-10">
          <DownloadForOffline fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      element: <TachographPopup closePopup={closePopup} isBottom={isBottom} />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      dataQaElementId: 'tachograph_file_download_menu_item',
      level: 1,
      path: '/tachograph/download',
    },
    {
      title: 'mainMenu.tachographSubmenu.chart',
      icon: (
        <NewFeatureBadge releaseDate="2026-01-10">
          <InsertChart fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      element: <TachographDiagramPopup closePopup={closePopup} isBottom={isBottom} />,
      showToken: false,
      mainFunction: true,
      showMobile: false,
      dataQaElementId: 'tachograph_chart_menu_item',
      level: 1,
      path: '/tachograph/chart',
    },
  ] satisfies PopupType[];

  return subMenu;
}

function contactSubmenu(closePopup: () => Promise<void>, isBottom: boolean): PopupType[] {
  const subMenu = [
    {
      title: 'mainMenu.contactSubmenu.contactInformationTitle',
      icon: <AlternateEmailOutlined fontSize={isBottom ? 'small' : 'medium'} />,
      element: <ContactsPopup closePopup={closePopup} />,
      showToken: true,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'contact_information_menu_item',
      level: 2,
      path: 'contact',
    },
    {
      title: 'mainMenu.contactSubmenu.feedback',
      icon: (
        <NewFeatureBadge releaseDate="2026-01-20">
          <EditNote fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      element: <FeedbackForm closePopup={closePopup} />,
      showToken: true,
      mainFunction: true,
      showMobile: false,
      dataQaElementId: 'feedback_menu_item',
      level: 2,
      path: 'feedback',
    },
  ] satisfies PopupType[];

  return subMenu;
}

function profileSubmenu(closePopup: () => Promise<void>, isBottom: boolean): PopupType[] {
  const subMenu = [
    {
      title: 'mainMenu.settings',
      icon: (
        <NewFeatureBadge releaseDate="2026-01-25">
          <SettingsOutlined fontSize={isBottom ? 'small' : 'medium'} />
        </NewFeatureBadge>
      ),
      element: <SettingsPopup closePopup={closePopup} data-qa-element-id="settings-popup" />,
      showToken: true,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-systemSettings',
      level: 2,
      path: 'settings',
    },
    {
      title: 'mainMenu.systemInformation',
      icon: <InfoOutlined fontSize={isBottom ? 'small' : 'medium'} />,
      element: <SysinfoPopup closePopup={closePopup} />,
      showToken: true,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-systemInfo',
      level: 2,
      path: 'sysinfo',
    },
    {
      title: 'mainMenu.logout',
      icon: <LogoutIcon fontSize={isBottom ? 'small' : 'medium'} />,
      element: <Logout />,
      showToken: false,
      mainFunction: false,
      showMobile: true,
      dataQaElementId: 'more-list-item-logout',
      level: 1,
      path: '/logout',
    },
  ] satisfies PopupType[];

  return subMenu;
}

export function popupDefs({
  closePopup,
  client,
  isBottom,
  user,
}: {
  closePopup: () => Promise<void>;
  client: QueryClient;
  isBottom: boolean;
  user: User;
}): PopupType[] {
  function logError(error: unknown, info: ErrorInfo) {
    console.log((error as Error).message);
    console.log(info);
  }

  // FIXME ez igy nem lazy loading ?
  const arr: PopupType[] = [
    {
      title: 'mainMenu.tracking',
      icon: <GpsFixed fontSize={isBottom ? 'small' : 'medium'} />,
      element: (
        <>
          <Outlet />
          <Map />
        </>
      ), // ? az jo h ebben van az Outlet ?
      showToken: isBottom, // desktopon nem mutatjuk a token usernek
      mainFunction: true,
      showMobile: true,
      level: 1,
      path: '/tracking',
      navigateTo: '/tracking/select',
    },
    {
      element: (
        <>
          <TrackingPopup data-qa-element-id="tracking-popup" />
          <Outlet />
        </>
      ),
      showToken: isBottom, // desktopon nem mutatjuk a token usernek
      mainFunction: true,
      showMobile: true,
      level: 2,
      path: '/tracking/select',
      hidden: true,
    },
    {
      element: <SharePopup />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: 3,
      path: '/tracking/select/share',
      hidden: true,
    },
    {
      title: 'mainMenu.routes',
      icon: <RouteOutlined fontSize={isBottom ? 'small' : 'medium'} sx={{ transform: 'rotate(90deg)' }} />,
      element: (
        <>
          <Outlet />
          <Map />
        </>
      ), // ? az jo h ebben van az Outlet ?,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: 1,
      path: '/routes',
      navigateTo: '/routes/select',
    },
    {
      element: (
        <ErrorBoundary FallbackComponent={Error} onError={logError} onReset={() => client.clear()}>
          <RoutesPopup data-qa-element-id="routes-popup" />
        </ErrorBoundary>
      ),
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: 2,
      path: '/routes/select',
      hidden: true,
    },
    {
      title: 'mainMenu.messages',
      icon:
        newMessageCount > 0 ? (
          <Badge color="secondary" variant="dot">
            <Message fontSize={isBottom ? 'small' : 'medium'} />
          </Badge>
        ) : (
          <Message fontSize={isBottom ? 'small' : 'medium'} />
        ),
      // FIXME ne a react=query teljes cache-t torolje ? csak a messages ?
      element: (
        <ErrorBoundary FallbackComponent={Error} onError={logError} onReset={() => client.clear()}>
          <MessagesPopup closePopup={closePopup} data-qa-element-id="messages-popup" />
        </ErrorBoundary>
      ),
      showToken: false,
      mainFunction: true,
      showMobile: true,
      show: (user?: User, chatId?: string) =>
        !user?.extraFeatures.includes('extrafeature_pluto_getstreamio_chat') || !chatId,
      level: isBottom ? 1 : 2, // mobilon nem kell ala terkep
      path: isBottom ? '/messages' : 'messages', // FIXME jobb lenne ha ez is /-el kezdodne mint minden mas
    },
    {
      title: 'mainMenu.streamMessages',
      icon: <StreamChatMenuIcon isBottom={isBottom} />,
      // FIXME ne a react=query teljes cache-t torolje ? csak a messages ?
      element: (
        <ErrorBoundary FallbackComponent={Error} onError={logError} onReset={() => client.clear()}>
          <StreamChatPopup closePopup={closePopup} data-qa-element-id="stream-messages-popup" />
        </ErrorBoundary>
      ),
      showToken: false,
      mainFunction: true,
      showMobile: true,
      show: (user?: User, chatId?: string) =>
        !!user && user.extraFeatures.includes('extrafeature_pluto_getstreamio_chat') && !!chatId,
      requiredExtraFeature: 'extrafeature_pluto_getstreamio_chat',
      level: isBottom ? 1 : 2, // mobilon nem kell ala terkep
      path: isBottom ? '/chat' : 'chat', // FIXME jobb lenne ha ez is /-el kezdodne mint minden mas
    },
    {
      title: 'mainMenu.refuels',
      icon: <LocalGasStation fontSize={isBottom ? 'small' : 'medium'} />,
      element: <RefuelsPopup closePopup={closePopup} data-qa-element-id="refuels-popup" />,
      showToken: false,
      mainFunction: true,
      showMobile: true,
      level: isBottom ? 1 : 2, // mobilon nem kell ala terkep
      path: isBottom ? '/refuels' : 'refuels',
    },
  ] satisfies PopupType[];

  let additionalElement: PopupType[];
  if (isBottom) {
    additionalElement = [
      {
        title: 'mainMenu.more',
        icon: (
          <NewFeatureBadge releaseDate="2026-01-20">
            <MoreHoriz fontSize="small" />
          </NewFeatureBadge>
        ),
        element: (
          <More closePopup={closePopup} hasTachograph={user.hasTachograph} data-qa-element-id="more-menu-list" />
        ),
        showToken: true,
        mainFunction: false,
        showMobile: true,
        level: 2,
        path: 'more',
        navigateTo: 'more',
      },
    ] satisfies PopupType[];
  } else {
    additionalElement = getAdditionalElements(closePopup, isBottom, user.hasTachograph);
  }

  return arr.concat(additionalElement);
}
